import { inject, Injectable } from '@angular/core';
import { ColorType } from '@app/shared/enums/color.enums';
import { IonicSafeString, ToastController, ToastOptions } from '@ionic/angular';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';

interface CustomToastOptions {
  header?: string;
  message?: string | IonicSafeString;
  key?: string; // keeping for now for backwards compatibility (to be deprecated)
  icon?: string;
  clickFn?: () => void | null;
}

@Injectable({
  providedIn: 'root',
})
export class ToastSandbox {
  private readonly toastController = inject(ToastController);
  private readonly translocoSandbox = inject(TranslationSandbox);

  readonly defaultConfig = {
    animated: true,
    duration: 5000,
  } as ToastOptions;

  readonly toastConfig = {
    cssClass: 'custom-toast',
    position: 'bottom',
    positionAnchor: 'c-tab-bar',
    swipeGesture: 'vertical',
    mode: 'md',
    buttons: [
      {
        icon: 'close',
        role: 'cancel',
        htmlAttributes: {
          'aria-label': 'close',
          'data-test-id': 'toast-sandbox.close-button.button',
        },
      },
    ],
  } as ToastOptions;

  readonly notificationConfig = {
    mode: 'ios',
    position: 'top',
    translucent: true,
  } as ToastOptions;

  showToast(options: CustomToastOptions): void {
    this.presentToast(
      this.getTranslatedMessage(options),
      {
        ...this.toastConfig,
        color: ColorType.LIGHT,
        icon: options.icon,
      },
      options.header
    );
  }

  showSuccessToast(options: CustomToastOptions): void {
    this.presentToast(
      this.getTranslatedMessage(options),
      {
        ...this.toastConfig,
        color: ColorType.SUCCESS,
      },
      options.header
    );
  }

  showErrorToast(options: CustomToastOptions): void {
    this.presentToast(
      this.getTranslatedMessage(options),
      {
        ...this.toastConfig,
        color: ColorType.DANGER,
        duration: 10000,
      },
      options.header
    );
  }

  showLocalNotification(options: CustomToastOptions): void {
    this.presentToast(
      this.getTranslatedMessage(options),
      {
        ...this.generateNotificationButtons(options),
        color: ColorType.LIGHT,
      },
      options.header
    );
  }

  private generateNotificationButtons(options: CustomToastOptions): ToastOptions {
    const config = this.notificationConfig;

    if (options?.clickFn) {
      config.buttons = [
        {
          icon: 'arrow-forward-outline',
          htmlAttributes: {
            'aria-label': 'navigate',
          },
          handler: (): void => options.clickFn(),
        },
      ];
    } else {
      config.buttons = null;
    }

    return config;
  }

  private async presentToast(
    message: string | IonicSafeString,
    customConfig: ToastOptions = {},
    header?: string
  ): Promise<void> {
    const modal = await this.toastController.create({
      ...this.defaultConfig,
      ...customConfig,
      header,
      message,
    });

    return modal.present();
  }

  private getTranslatedMessage(options: CustomToastOptions): string | IonicSafeString {
    return options.key ? this.translocoSandbox.translate(options.key) : options.message;
  }
}
